import { __assign, __awaiter, __generator } from "tslib";
import { DefaultMajorVersion, getCentroUrl, initializeLogging, LogError, LogInfo, } from '@ms/ocv-inapp-feedback-shared';
import { FeedbackUiType } from '../types';
import { IN_APP_FEEDBACK_LOADER_VERSION } from './CompileTimeConstants';
import { inappFeedbackLoaderVersionPrefix, OcvInAppFeedback } from './Constants';
export function validateInitializationParameters(initializationInfo) {
    return __awaiter(this, void 0, void 0, function () {
        var inAppFeedbackRef, hostName, locale, missingParameterError, errorMessageList, parameterError;
        return __generator(this, function (_a) {
            inAppFeedbackRef = initializationInfo.inAppFeedbackRef, hostName = initializationInfo.hostName, locale = initializationInfo.locale;
            missingParameterError = 'is a mandatory parameter';
            errorMessageList = [];
            if (!inAppFeedbackRef) {
                errorMessageList.push("inAppFeedbackRef ".concat(missingParameterError));
            }
            if (!hostName) {
                errorMessageList.push("hostName ".concat(missingParameterError));
                initializationInfo.hostName = 'OcvFeedbackDemoHostingApp'; // setting a default value to make sure initialize is not failing
            }
            if (!locale) {
                errorMessageList.push("locale ".concat(missingParameterError));
                initializationInfo.locale = 'en'; // setting a default value to make sure initialize is not failing
            }
            if (errorMessageList.length > 0) {
                // This is to enable dynamic logging endpoint to be GDPR and EUDB compliant
                initializeLogging({
                    centroEnvironmentBaseUrl: getCentroUrl(initializationInfo.cloudType),
                    hostName: initializationInfo.hostName,
                });
                parameterError = new Error(JSON.stringify(errorMessageList));
                LogError("532379" /* LoggingTags.FeedbackInitDataValidationError */, 'FeedbackInitData Validation Error: ', parameterError, {});
                if (initializationInfo.onInitializationError) {
                    initializationInfo.onInitializationError(parameterError);
                }
                throw parameterError;
            }
            return [2 /*return*/];
        });
    });
}
var buildInAppFeedbackLoaderVersionString = function (sdkVersion) {
    var inappFeedbackLoaderVersionString = "".concat(inappFeedbackLoaderVersionPrefix).concat(IN_APP_FEEDBACK_LOADER_VERSION);
    if (!sdkVersion) {
        return inappFeedbackLoaderVersionString;
    }
    // `setInitTelemetryData()` can sometimes be called multiple times and so this avoids duplicating the
    // `inappFeedbackLoaderVersionString` if it is already in the `sdkVersion`
    if (sdkVersion.includes(inappFeedbackLoaderVersionPrefix)) {
        return sdkVersion;
    }
    return "".concat(sdkVersion, " | ").concat(inappFeedbackLoaderVersionString);
};
/**
 * This function adds common data to the feedback init options
 * Currently adds hvcInvocationTimestamp
 * To be added: inapp-feedback loader version
 * @param iFeedbackInitOptions
 * @returns iFeedbackInitOptions
 */
export var setInitTelemetryData = function (iFeedbackInitOptions) {
    var _a, _b, _c, _d, _e, _f;
    var sdkVersion = buildInAppFeedbackLoaderVersionString((_a = iFeedbackInitOptions.telemetry) === null || _a === void 0 ? void 0 : _a.sdkVersion);
    if ((_b = iFeedbackInitOptions.feedbackConfig) === null || _b === void 0 ? void 0 : _b.isDisplayed) {
        iFeedbackInitOptions.performanceTimestamps = __assign(__assign({}, iFeedbackInitOptions.performanceTimestamps), { hvcInvocationTimestamp: Date.now() });
    }
    /**
     * Log the request to open the feedback form
     * The params in the validation are the primary params that are required to open the feedback form
     */
    if (((_c = iFeedbackInitOptions.feedbackConfig) === null || _c === void 0 ? void 0 : _c.isDisplayed) ||
        ((_d = iFeedbackInitOptions.feedbackConfig) === null || _d === void 0 ? void 0 : _d.feedbackUiType) === FeedbackUiType.NoSurface ||
        ((_e = iFeedbackInitOptions.feedbackConfig) === null || _e === void 0 ? void 0 : _e.feedbackUiType) === FeedbackUiType.NoSurfaceWithoutTitle) {
        LogInfo("532390" /* LoggingTags.OpenFeedbackFormRequested */, 'Open Feedback Form Requested', {
            clientFeedbackId: (_f = iFeedbackInitOptions.clientFeedbackId) !== null && _f !== void 0 ? _f : null,
            sdkVersion: sdkVersion,
        });
    }
    if (iFeedbackInitOptions.telemetry) {
        iFeedbackInitOptions.telemetry.sdkVersion = sdkVersion;
    }
    else {
        iFeedbackInitOptions.telemetry = { sdkVersion: sdkVersion };
    }
    return iFeedbackInitOptions;
};
/**
 * Returns the Centro init parameters based on the initialization info provided.
 * Only one of localMode, pkgver, bldver can be set, preferred in that order. Eg.: if localMode is true then pkgver and bldver would be undefined
 * If localmode is true bundles are fetched from localcdn.centro-dev.com:5555
 * @param initializationInfo
 * @returns Centro init parameters
 */
export var getCentroInitParameters = function (initializationInfo) {
    var hostName = initializationInfo.hostName, locale = initializationInfo.locale, centroUrl = initializationInfo.centroUrl, cloudType = initializationInfo.cloudType;
    var _a = fetchBundleVersionParams(initializationInfo), localMode = _a.localMode, pkgver = _a.pkgver, bldver = _a.bldver;
    return {
        featureName: OcvInAppFeedback,
        hostName: hostName,
        centroEnvironmentBaseUrl: centroUrl !== null && centroUrl !== void 0 ? centroUrl : getCentroUrl(cloudType),
        locale: locale,
        localMode: localMode,
        pkgver: pkgver,
        bldver: bldver,
    };
};
/**
 * * Only one of localMode, pkgver, bldver can be set, preferred in that order. Eg.: if localMode is true then pkgver and bldver would be undefined
 * @param initializationInfo
 * @returns { localMode, specificHvcVersion, majorVersion }
 */
var fetchBundleVersionParams = function (initializationInfo) {
    var localMode = initializationInfo.localMode, specificHvcVersion = initializationInfo.specificHvcVersion, majorVersion = initializationInfo.majorVersion;
    var isLocal = localMode || getQueryParamsValue('locdbg', 'true');
    if (isLocal) {
        return { localMode: isLocal, pkgver: undefined, bldver: undefined };
    }
    if (!!specificHvcVersion) {
        return { localMode: false, pkgver: specificHvcVersion, bldver: undefined };
    }
    if (!!majorVersion) {
        return { localMode: false, pkgver: undefined, bldver: majorVersion };
    }
    return { localMode: false, pkgver: undefined, bldver: DefaultMajorVersion };
};
var getQueryParamsValue = function (searchKey, searchValue) {
    var _a;
    if (!((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.search)) {
        return false;
    }
    var queryParams = new URLSearchParams(window.location.search);
    return queryParams.get(searchKey) === searchValue;
};
